import React, { Component, ErrorInfo, ReactNode } from "react";
import { Box, Typography, Button, Paper } from "@mui/material";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    // Mettre à jour l'état pour que le prochain rendu affiche l'UI de secours
    return { hasError: true, error, errorInfo: null };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Uncaught error:", error, errorInfo);
    this.setState({
      error,
      errorInfo,
    });
  }

  public resetError = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <Paper
          sx={{
            p: 4,
            m: 2,
            maxWidth: "800px",
            mx: "auto",
            mt: "20vh",
            backgroundColor: "#fff8f8",
            border: "1px solid #ffcccc",
          }}
        >
          <Typography variant="h4" color="error" gutterBottom>
            Une erreur s'est produite
          </Typography>

          <Box my={2}>
            <Typography variant="body1" gutterBottom>
              {this.state.error?.message || "Erreur inconnue"}
            </Typography>
          </Box>

          {this.state.errorInfo && (
            <Box
              my={2}
              p={2}
              sx={{
                backgroundColor: "#f5f5f5",
                borderRadius: 1,
                overflowX: "auto",
              }}
            >
              <Typography
                variant="body2"
                component="pre"
                sx={{ whiteSpace: "pre-wrap" }}
              >
                {this.state.errorInfo.componentStack}
              </Typography>
            </Box>
          )}

          <Box mt={3} display="flex" gap={2}>
            <Button
              variant="contained"
              color="primary"
              onClick={this.resetError}
            >
              Réessayer
            </Button>

            <Button
              variant="outlined"
              onClick={() => (window.location.href = "/")}
            >
              Retour à l'accueil
            </Button>
          </Box>
        </Paper>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
